import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import queryString from 'query-string';

import { Loading } from '@autopass-web/mochi-component';
import { SecurityFillIcon } from '@autopass-web/mochi-icons';

import { CreditCardInputForm } from '../components/credit-card-input-form';
import { Modal, useModal } from '../components/modal';
import { ActionButton, Block, PageLayout } from '../components/UI';
import { scheme } from '../components/validate-scheme';
import { environment } from '../environments/environment';
import { useEnhancedNavigate } from '../routes/navigate';
import { analyticsService } from '../services/amplitude';
import client from '../services/client';
import { useCreditCardValidate } from '../services/use-credit-card-validate';
import { useRefetchUserMe } from '../services/use-user-me';
import { userEditStore } from '../store/user-store';

const { DOWNLOAD_AUTOPASS_LINK } = environment;

type FormData = {
  creditCard: {
    number: string;
    cvc: string;
    exp: string;
  };
};

const MERCHANT_TRANSACTION_ID_KEY = 'merchant_transaction_id';
const TOKEN_KEY = 'token';

const PaymentErrorModal = (props) => {
  return (
    <Modal
      {...{
        ...props,
        title: '信用卡授權失敗',
        closeActionText: '返回編輯',
        children: '請重新檢視信用卡資訊，並重新授權。',
      }}
    />
  );
};

export function PaymentAuth() {
  const refetch = useRefetchUserMe();
  const navigate = useEnhancedNavigate();
  const modal = useModal();
  const checkRef = useRef(false);
  useEffect(() => {
    const checkBindSuccessfully = async () => {
      try {
        if (checkRef.current) {
          return;
        }
        const {
          [MERCHANT_TRANSACTION_ID_KEY]: transactionId,
          [TOKEN_KEY]: token,
        } = queryString.parse(window.location.hash);

        await client.createTransactionToken({
          token: token as string,
          transactionId: transactionId as string,
        });

        // clean up auth queries
        const hash = queryString.parse(window.location.hash);

        delete hash[MERCHANT_TRANSACTION_ID_KEY];
        delete hash[TOKEN_KEY];

        const nextHash = queryString.stringify(hash);

        navigate({ hash: nextHash }, { replace: true, merge: true });

        checkRef.current = true;

        await refetch();
      } catch (err) {
        modal.onOpen();
      }
    };

    checkBindSuccessfully();
  }, [modal, navigate, refetch]);

  return (
    <>
      <PaymentErrorModal {...modal} />
      <div>
        <Loading />
      </div>
    </>
  );
}

function PaymentConfig({
  prefillNumber,
  prefillExp,
}: {
  prefillNumber: string | undefined;
  prefillExp: string | undefined;
}) {
  const modal = useModal();

  const methods = useForm<FormData>({
    resolver: zodResolver(scheme),
    values: {
      creditCard: {
        number: prefillNumber,
        exp: prefillExp,
        cvc: undefined,
      },
    },
  });
  const { mutate: validateCreditCard, isPending } = useCreditCardValidate();

  // here will redirect to credit card page, so dont need redirect.
  const handleSubmit = methods.handleSubmit((data) => {
    validateCreditCard(
      {
        cardCvc: data.creditCard.cvc,
        cardExpireDate: data.creditCard.exp,
        cardNumber: data.creditCard.number,
        callbackUrl: window.location.href,
      },
      {
        onError: (err) => {
          // todo
          console.log(err);
          modal.onOpen();
        },
        onSuccess: () => {
          userEditStore.edit();
        },
      },
    );
  });

  useEffect(() => {
    analyticsService.creditCardSetupPageview();
  }, []);

  const actions = (
    <>
      <ActionButton
        isLoading={isPending}
        onClick={() => {
          analyticsService.creditCardJoinMachiClick();
        }}
      >
        成為麻吉！
      </ActionButton>
      <div className={'flex items-start mt-3 w-[calc(100%-50px)] text-[13px]'}>
        <SecurityFillIcon width={20} height={20} color="#00C486" />
        <p className={'ml-1 font-light'}>
          Autopass 採用 TLS 1.2 2048bit
          加密技術，不會透露您的實際卡片資訊予第三方，致力保障用卡安全無虞。
        </p>
      </div>
    </>
  );

  return (
    <>
      <PaymentErrorModal {...modal} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <PageLayout
            title={'一次設定，麻吉處處都行'}
            desc="這是車牌辨識自動出入停車場的最後一步"
            bottom={actions}
          >
            <Block title="付款資訊">
              <CreditCardInputForm
                onCardNumberFocus={analyticsService.creditCardNumberClick}
                onExpiredDateFocus={analyticsService.creditCardExpiryClick}
                onCvcFocus={analyticsService.creditCardCvcClick}
              />
              <p className={'text-[13px]'}>
                新增卡片時將產生一筆台幣 1
                元的過卡交易，不會實際請款，只有在停車消費時才會扣款，並可隨時在
                <a
                  href={DOWNLOAD_AUTOPASS_LINK}
                  target="_blank"
                  rel="noreferrer"
                >
                  車麻吉 APP
                </a>{' '}
                內移除綁定。
              </p>
            </Block>
          </PageLayout>
        </form>
      </FormProvider>
    </>
  );
}

export const PaymentPage = () => {
  const {
    merchant_transaction_id: transactionId,
    token,
    exp,
    number,
  } = queryString.parse(window.location.hash);

  if (transactionId && token) {
    return <PaymentAuth />;
  }

  const prefillExp = typeof exp === 'string' ? exp : undefined;
  const prefillNumber = typeof number === 'string' ? number : undefined;

  return (
    <PaymentConfig prefillExp={prefillExp} prefillNumber={prefillNumber} />
  );
};
